import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { RoleTag, WorkflowTag } from "@/components/MultiUserSelect";

/**
 * TL slot inside Add-Team dialog — a single "Content TL" or "Publishing TL"
 * panel with a Create-new / Pick-existing toggle. Existing pool is filtered
 * to current Content-dept Team Leads not already under this CM.
 * Internal to AddNodeDialog.
 */
function TLSlot({ slot, setSlot, label, required, helpNote, tintClass, labelClass, testPrefix, candidatePool = [], pickerParentId }) {
  const [search, setSearch] = useState("");
  const setMode = (m) => setSlot({ ...slot, mode: m });
  const patch = (k, v) => setSlot({ ...slot, [k]: v });

  const candidates = useMemo(() => {
    // Iter 36.2: Only role-based exclusion — never demote a CM or an
    // admin/super-admin. Anyone else visible is fair game, even users
    // already sitting under this CM (they'll be reassigned by PATCH so
    // admins can move an associate up to TL, or shuffle a TL between
    // teams).
    const skipRoles = new Set(["content_manager", "admin", "super_admin"]);
    return (candidatePool || []).filter((u) => !skipRoles.has(u.role));
  }, [candidatePool]);
  const filtered = useMemo(() => {
    if (!search) return candidates;
    const q = search.toLowerCase();
    return candidates.filter((u) => u.name.toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q));
  }, [candidates, search]);

  return (
    <div className={`rounded-lg border ${tintClass} p-3 space-y-2`}>
      <div className="flex items-center justify-between gap-2">
        <div className={`text-xs font-semibold uppercase tracking-wider ${labelClass}`}>
          {label}{required && <span className="text-red-500 ml-1">*</span>}
          {helpNote && <span className="text-slate-400 font-normal normal-case ml-1">{helpNote}</span>}
        </div>
        <div className="inline-flex bg-white/70 border border-slate-200 rounded-md p-0.5 gap-0.5">
          <button
            type="button"
            onClick={() => setMode("new")}
            className={`px-2 py-0.5 rounded text-[10px] font-medium transition ${slot.mode === "new" ? "bg-purple-600 text-white" : "text-slate-600 hover:text-purple-700"}`}
            data-testid={`${testPrefix}-mode-new`}
          >Create new</button>
          <button
            type="button"
            onClick={() => setMode("existing")}
            className={`px-2 py-0.5 rounded text-[10px] font-medium transition ${slot.mode === "existing" ? "bg-purple-600 text-white" : "text-slate-600 hover:text-purple-700"}`}
            data-testid={`${testPrefix}-mode-existing`}
          >Pick existing</button>
        </div>
      </div>

      {slot.mode === "existing" ? (
        <>
          <Input
            placeholder="Search name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-xs"
            data-testid={`${testPrefix}-search`}
          />
          <div className="max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-white divide-y divide-slate-100">
            {filtered.length === 0 && (
              <div className="p-3 text-[11px] text-slate-400 text-center italic">
                {candidates.length === 0 ? "No other team members visible to pick from." : "No matches."}
              </div>
            )}
            {filtered.map((u) => (
              <label
                key={u.id}
                className={`flex items-center gap-2 px-2.5 py-1.5 cursor-pointer hover:bg-slate-50 ${slot.existing_id === u.id ? "bg-purple-50" : ""}`}
                data-testid={`${testPrefix}-pick-${u.id}`}
              >
                <input
                  type="radio"
                  checked={slot.existing_id === u.id}
                  onChange={() => patch("existing_id", u.id)}
                  className="accent-purple-600"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-xs font-medium text-slate-800 truncate">{u.name}</span>
                    <RoleTag role={u.role} tl_kind={u.tl_kind} />
                    <WorkflowTag type={u.workflow_type} />
                  </div>
                  <div className="text-[10px] text-slate-500 truncate">{u.email}</div>
                </div>
                {u.team_name && (
                  <span className="text-[9px] text-slate-500 uppercase tracking-wider">was {u.team_name}</span>
                )}
              </label>
            ))}
          </div>
          {slot.existing_id && (
            <p className="text-[10px] text-slate-500 italic">
              This will move the selected TL (and their associates) into this team.
            </p>
          )}
        </>
      ) : (
        <>
          <div>
            <Label className="text-xs">Name</Label>
            <Input value={slot.name} onChange={(e) => patch("name", e.target.value)} data-testid={`${testPrefix}-name`} />
          </div>
          <div>
            <Label className="text-xs">Email</Label>
            <Input type="email" value={slot.email} onChange={(e) => patch("email", e.target.value)} data-testid={`${testPrefix}-email`} />
          </div>
          <div>
            <Label className="text-xs">Password</Label>
            <Input type="text" value={slot.password} onChange={(e) => patch("password", e.target.value)} data-testid={`${testPrefix}-password`} />
          </div>
        </>
      )}
    </div>
  );
}

/**
 * AddNodeDialog — modal for adding a CM / TL / Team / Project under a
 * hierarchy node. Handles both "create new user" and "pick existing" modes,
 * as well as the special "team" kind which spawns Content-TL + Publishing-TL
 * sharing one team_name.
 */
export default function AddNodeDialog({ dialog, users = [], onClose, onDone }) {
  const [mode, setMode] = useState("new"); // "new" | "existing"
  const [form, setForm] = useState({ name: "", email: "", password: "", site_url: "", department: "content" });
  const [teamForm, setTeamForm] = useState({
    team_name: "",
    content: { mode: "new", name: "", email: "", password: "", existing_id: "" },
    publishing: { mode: "new", name: "", email: "", password: "", existing_id: "" },
  });
  const [pickerId, setPickerId] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    setForm({ name: "", email: "", password: "", site_url: "", department: dialog?.parent?.department || "content" });
    setTeamForm({
      team_name: "",
      content: { mode: "new", name: "", email: "", password: "", existing_id: "" },
      publishing: { mode: "new", name: "", email: "", password: "", existing_id: "" },
    });
    // CM kind is "Pick existing" only (iter 36.2) — force the mode so the
    // dialog opens straight into the picker regardless of prior state.
    setMode(dialog?.kind === "cm" ? "existing" : "new");
    setPickerId(""); setSearch("");
  }, [dialog]);

  const candidates = useMemo(() => {
    if (!dialog) return [];
    const topTier = new Set(["super_admin", "admin"]);
    if (dialog.kind === "cm") {
      return users.filter((u) => !topTier.has(u.role));
    }
    if (dialog.kind === "tl") {
      const parentId = dialog?.parent?.id;
      return users.filter((u) => !topTier.has(u.role) && u.role !== "content_manager" && u.id !== parentId && u.manager_id !== parentId);
    }
    return [];
  }, [dialog, users]);

  const filteredCandidates = useMemo(() => {
    if (!search) return candidates;
    const s = search.toLowerCase();
    return candidates.filter((u) => u.name.toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s));
  }, [candidates, search]);

  if (!dialog) return null;

  const isUserKind = dialog.kind === "cm" || dialog.kind === "tl";

  const submit = async () => {
    try {
      let expandKeys = [];
      if (dialog.kind === "cm") {
        if (mode === "existing") {
          if (!pickerId) { toast.error("Pick a member to promote"); return; }
          await api.patch(`/users/${pickerId}`, {
            role: "content_manager",
            position: "Content Manager",
            department: dialog.parent.department || "content",
            manager_id: "",
            team_lead_id: "",
            team_name: "",
            tl_kind: "",
          });
          expandKeys.push(`dept-${dialog.parent.department || "content"}`, `cm-${pickerId}`);
        } else {
          if (!form.name || !form.email || !form.password) { toast.error("Fill all fields"); return; }
          const res = await api.post("/auth/register", {
            name: form.name, email: form.email, password: form.password,
            role: "content_manager", department: dialog.parent.department || "content",
          });
          const newId = res?.data?.id;
          expandKeys.push(`dept-${dialog.parent.department || "content"}`);
          if (newId) expandKeys.push(`cm-${newId}`);
        }
      } else if (dialog.kind === "tl") {
        if (mode === "existing") {
          if (!pickerId) { toast.error("Pick a member to assign"); return; }
          await api.patch(`/users/${pickerId}`, {
            role: "team_lead",
            position: "Content Team Lead",
            manager_id: dialog.parent.id,
            department: dialog.parent.department || "content",
          });
        } else {
          if (!form.name || !form.email || !form.password) { toast.error("Fill all fields"); return; }
          await api.post("/auth/register", {
            name: form.name, email: form.email, password: form.password,
            role: "team_lead", department: dialog.parent.department || "content",
            manager_id: dialog.parent.id,
          });
        }
        expandKeys.push(`cm-${dialog.parent.id}`);
      } else if (dialog.kind === "team") {
        const t = teamForm;
        const teamName = (t.team_name || "").trim();
        if (!teamName) { toast.error("Team name required"); return; }

        const validateSlot = (slot, label, required) => {
          if (slot.mode === "existing") {
            if (!slot.existing_id) return required ? `Pick an existing ${label}` : null;
            return null;
          }
          const anyFilled = !!(slot.name || slot.email || slot.password);
          if (!required && !anyFilled) return null;
          if (!slot.name || !slot.email || !slot.password) return `${label} name, email & password required`;
          return null;
        };
        const applySlot = async (slot, tl_kind) => {
          if (slot.mode === "existing") {
            if (!slot.existing_id) return;
            await api.patch(`/users/${slot.existing_id}`, {
              manager_id: dialog.parent.id,
              department: "content",
              team_name: teamName,
              tl_kind,
              role: "team_lead",
            });
          } else {
            const anyFilled = !!(slot.name || slot.email || slot.password);
            if (!anyFilled) return;
            await api.post("/auth/register", {
              name: slot.name, email: slot.email, password: slot.password,
              role: "team_lead", department: "content",
              manager_id: dialog.parent.id,
              team_name: teamName, tl_kind,
            });
          }
        };

        // Pre-check: warn upfront if picking an existing TL who's already
        // in a different team.
        const preCheckSlot = (slot, kindLabel) => {
          if (slot.mode !== "existing" || !slot.existing_id) return true;
          const picked = users.find((u) => u.id === slot.existing_id);
          const currentTeam = (picked?.team_name || "").trim();
          if (!picked || !currentTeam || currentTeam === teamName) return true;
          const currentKind = picked.tl_kind === "publishing" ? "Publishing" : "Content";
          return window.confirm(
            `${picked.name} is currently the ${currentKind} TL of "${currentTeam}".\n\n` +
            `Adding them as ${kindLabel} TL of "${teamName}" will REMOVE them from "${currentTeam}" ` +
            `(a Team Lead can only belong to one team at a time).\n\nContinue?`
          );
        };

        const cErr = validateSlot(t.content, "Content TL", true);
        if (cErr) { toast.error(cErr); return; }
        const pErr = validateSlot(t.publishing, "Publishing TL", false);
        if (pErr) { toast.error(pErr); return; }

        if (!preCheckSlot(t.content, "Content")) return;
        if (!preCheckSlot(t.publishing, "Publishing")) return;

        // Persist the team row FIRST so it survives even if all TLs leave.
        try {
          await api.post("/teams", {
            name: teamName,
            cm_id: dialog.parent.id,
            department: dialog.parent.department || "content",
          });
        } catch (e) { /* not fatal — legacy derived team still works */ }

        await applySlot(t.content, "content");
        await applySlot(t.publishing, "publishing");
        expandKeys.push(`cm-${dialog.parent.id}`, `team-team-${dialog.parent.id}-${teamName}`);
      } else if (dialog.kind === "project") {
        if (!form.name) { toast.error("Name required"); return; }
        await api.post("/projects", {
          name: form.name, site_url: form.site_url, team_lead_ids: [dialog.parent.id],
          department: dialog.parent.department || "content",
        });
      }
      toast.success(mode === "existing" ? "Assigned" : "Created");
      onDone({ expandKeys });
    } catch (e) {
      if (e && e.message === "cancelled") return;
      toast.error(formatApiError(e));
    }
  };

  const titles = {
    cm: "Promote to Content Manager",
    tl: `Add Team Lead under ${dialog?.parent?.name || ""}`,
    team: `Create Team under ${dialog?.parent?.name || ""}`,
    project: `Add Project under ${dialog?.parent?.name || ""}`,
  };

  return (
    <Dialog open={!!dialog} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{titles[dialog.kind]}</DialogTitle></DialogHeader>

        {isUserKind && dialog.kind !== "cm" && (
          <div className="inline-flex bg-slate-100 rounded-lg p-1 gap-0.5 self-start" data-testid="add-node-mode-toggle">
            <button
              onClick={() => setMode("new")}
              className={`px-3 py-1 rounded text-xs font-medium transition ${mode === "new" ? "bg-white text-purple-700 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
              data-testid="add-node-mode-new"
            >Create new</button>
            <button
              onClick={() => setMode("existing")}
              className={`px-3 py-1 rounded text-xs font-medium transition ${mode === "existing" ? "bg-white text-purple-700 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
              data-testid="add-node-mode-existing"
            >Pick existing</button>
          </div>
        )}

        <div className="space-y-3">
          {isUserKind && mode === "existing" ? (
            <>
              <div>
                <Label>Search team members</Label>
                <Input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Type a name or email…" className="mt-1" data-testid="add-node-existing-search" />
                <p className="text-[10px] text-slate-500 mt-1">
                  {dialog.kind === "cm"
                    ? "Pick any team member — they'll be promoted to Content Manager of this department."
                    : "Pick any visible team member — they'll be assigned as Team Lead here."}
                </p>
              </div>
              <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
                {filteredCandidates.length === 0 && (
                  <div className="p-4 text-xs text-slate-500 text-center">
                    {candidates.length === 0
                      ? (dialog.kind === "cm"
                          ? "No team members available. Add one first via the People page, then come back to promote them."
                          : "No team members available. Add one via the People page, or use “Create new” below.")
                      : "No matches for your search."}
                  </div>
                )}
                {filteredCandidates.map((u) => (
                  <label key={u.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50 ${pickerId === u.id ? "bg-purple-50" : ""}`} data-testid={`add-node-pick-${u.id}`}>
                    <input type="radio" name="pick" checked={pickerId === u.id} onChange={() => setPickerId(u.id)} className="accent-purple-600" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-sm font-medium text-slate-800 truncate">{u.name}</span>
                        <RoleTag role={u.role} tl_kind={u.tl_kind} />
                        <WorkflowTag type={u.workflow_type} />
                      </div>
                      <div className="text-xs text-slate-500 truncate">{u.email}</div>
                    </div>
                  </label>
                ))}
              </div>
              {pickerId && (
                <p className="text-[11px] text-slate-500">
                  {dialog.kind === "cm"
                    ? <>This will promote the selected member to <strong>Content Manager</strong> of this department.</>
                    : <>This will move the selected member (and their associates, if any) under <strong>{dialog?.parent?.name}</strong> as Team Lead.</>}
                </p>
              )}
            </>
          ) : dialog.kind === "team" ? (
            <>
              <div>
                <Label>Team name <span className="text-red-500">*</span></Label>
                <Input
                  autoFocus
                  value={teamForm.team_name}
                  onChange={(e) => setTeamForm({ ...teamForm, team_name: e.target.value })}
                  placeholder="e.g. Team Batman"
                  data-testid="add-team-name"
                />
              </div>
              <TLSlot
                slot={teamForm.content}
                setSlot={(next) => setTeamForm({ ...teamForm, content: next })}
                label="Content TL"
                required
                tintClass="border-emerald-200 bg-emerald-50/40"
                labelClass="text-emerald-700"
                testPrefix="add-team-content"
                candidatePool={users}
                pickerParentId={dialog?.parent?.id}
              />
              <TLSlot
                slot={teamForm.publishing}
                setSlot={(next) => setTeamForm({ ...teamForm, publishing: next })}
                label="Publishing TL"
                required={false}
                helpNote="(optional — add later from People page)"
                tintClass="border-purple-200 bg-purple-50/40"
                labelClass="text-purple-700"
                testPrefix="add-team-publishing"
                candidatePool={users}
                pickerParentId={dialog?.parent?.id}
              />
            </>
          ) : (
            <>
              <div>
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="add-node-name" />
              </div>
              {dialog.kind === "project" ? (
                <div>
                  <Label>Site URL (optional)</Label>
                  <Input value={form.site_url} onChange={(e) => setForm({ ...form, site_url: e.target.value })} placeholder="https://…" />
                </div>
              ) : (
                <>
                  <div>
                    <Label>Email</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="add-node-email" />
                  </div>
                  <div>
                    <Label>Password</Label>
                    <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="add-node-password" />
                  </div>
                </>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="bg-purple-600 hover:bg-purple-700" data-testid="add-node-submit">
            {mode === "existing" ? "Assign" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
